import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import ReactDOM from 'react-dom';
import { BACKEND_URL } from '../../../Config/Const';
import { useNavigation } from '@react-navigation/native';
import { colores, fuentes } from '../../../styles/variablesCss';

// Función para capitalizar texto
const capitalizeText = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const sortByOrderThenName = (items = [], nameKey = 'nombre') => {
  const safeItems = Array.isArray(items) ? [...items] : [];
  return safeItems.sort((a = {}, b = {}) => {
    const orderA = typeof a.orden === 'number' ? a.orden : null;
    const orderB = typeof b.orden === 'number' ? b.orden : null;

    if (orderA !== null && orderB !== null && orderA !== orderB) {
      return orderA - orderB;
    }
    if (orderA !== null && orderB === null) {
      return -1;
    }
    if (orderA === null && orderB !== null) {
      return 1;
    }

    const nameA = (a[nameKey] || '').toString().toUpperCase();
    const nameB = (b[nameKey] || '').toString().toUpperCase();
    return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
  });
};

const sortHierarchy = (departments = []) => {
  return sortByOrderThenName(departments, 'departamento').map((dept) => ({
    ...dept,
    categorias: sortByOrderThenName(dept?.categorias, 'categoria').map((cat) => ({
      ...cat,
      subCategorias: sortByOrderThenName(cat?.subCategorias, 'subcategoria'),
    })),
  }));
};

const Categorias = ({ visible, onClose, onCategorySelect, anchorPosition }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigation = useNavigation();
  const containerRef = useRef(null);

  // Estados para controlar quÃ© nivel estÃ¡ abierto (hover)
  const [hoveredDepartment, setHoveredDepartment] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredSubcategory, setHoveredSubcategory] = useState(null);
  const [hoveredCategoryForZoom, setHoveredCategoryForZoom] = useState(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!visible || Platform.OS !== 'web') return;
    
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  // Restaurar Ãºltima selecciÃ³n cuando se abre, o resetear cuando se cierra
  useEffect(() => {
    if (visible && Platform.OS === 'web' && departments.length > 0) {
      // Restaurar Ãºltima selecciÃ³n desde localStorage
      try {
        const lastSelection = localStorage.getItem('lastCategorySelection');
        if (lastSelection) {
          const { deptId, catIndex } = JSON.parse(lastSelection);
          // Verificar que el departamento existe
          const deptExists = departments.find(d => d.cod === deptId);
          if (deptExists) {
            setHoveredDepartment(deptId);
            if (catIndex !== null && catIndex !== undefined) {
              setHoveredCategory(catIndex);
            }
          }
        }
      } catch (e) {
        //console.log('Error restaurando selecciÃ³n de categorÃ­a:', e);
      }
    } else if (!visible) {
      // Resetear al cerrar
      setHoveredDepartment(null);
      setHoveredCategory(null);
    }
  }, [visible, departments]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      if (visible && departments.length === 0) {
        setLoading(true);
        setError(null);

        try {
          const response = await fetch(`${BACKEND_URL}/rest/productos/categorias`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
          }

          const data = await response.json();

          if (data.dsResultado && data.dsResultado.departamentos) {
            const sortedDepartments = sortHierarchy(data.dsResultado.departamentos);
            setDepartments(sortedDepartments);
          } else {
            setError('Formato de respuesta inesperado');
          }
        } catch (err) {
          console.error('Error fetching categories:', err);
          setError('No se pudieron cargar las categorÃ­as');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCategories();
  }, [visible]);

  // Obtener datos activos segÃºn hover
  const activeDept = departments.find(dept => dept.cod === hoveredDepartment);
  const activeCategories = activeDept?.categorias || [];
  const activeCategory = hoveredCategory !== null ? activeCategories.find((c, idx) => idx === hoveredCategory) : null;
  const activeSubcategories = activeCategory?.subCategorias || [];

  // Función para generar URL de categoría
  const getCategoryUrl = (category) => {
    const deptIdForSearch =
      category.codDepto === 0 || category.codDepto
        ? category.codDepto
        : hoveredDepartment;
    const categoryId = category.codcat;
    const currentDept = departments.find(dept => dept.cod === hoveredDepartment);
    const deptName = currentDept?.departamento || '';
    const catName = category.categoria || '';
    const ofe = !!category['oyc-rdw'];

    const searchParams = new URLSearchParams();
    searchParams.set('D', String(deptIdForSearch));
    searchParams.set('C', String(categoryId));
    searchParams.set('S', '0');
    searchParams.set('deptName', deptName);
    searchParams.set('catName', catName);
    searchParams.set('subcatName', '');
    searchParams.set('codBanner', '0');
    if (ofe) searchParams.set('ofe', 'true');

    return `/productos?${searchParams.toString()}`;
  };

  // Función para generar URL de subcategoría
  const getSubcategoryUrl = (subcat) => {
    const deptId =
      activeCategory?.codDepto === 0 || activeCategory?.codDepto
        ? activeCategory?.codDepto
        : subcat.codDepto;
    const catId = activeCategory?.codcat || subcat.codcat;
    const subcatId = subcat.codsubcat;
    const deptName = activeDept?.departamento || '';
    const catName = activeCategory?.categoria || '';
    const subcatName = subcat.subcategoria || '';

    const searchParams = new URLSearchParams();
    searchParams.set('D', String(deptId));
    searchParams.set('C', String(catId));
    searchParams.set('S', String(subcatId));
    searchParams.set('deptName', deptName);
    searchParams.set('catName', catName);
    searchParams.set('subcatName', subcatName);
    searchParams.set('codBanner', '0');

    return `/productos?${searchParams.toString()}`;
  };

  // Handler para click en categoría
  const handleCategoryClick = (category) => {
    const deptIdForSearch =
      category.codDepto === 0 || category.codDepto
        ? category.codDepto
        : hoveredDepartment;
    const categoryId = category.codcat;
    const currentDept = departments.find(dept => dept.cod === hoveredDepartment);
    const deptName = currentDept?.departamento || '';
    const catName = category.categoria || '';
    const ofe = !!category['oyc-rdw'];

    // Guardar última selección en localStorage
    if (Platform.OS === 'web') {
      try {
        const catIndex = activeCategories.findIndex(c => c.codcat === categoryId);
        localStorage.setItem('lastCategorySelection', JSON.stringify({
          deptId: hoveredDepartment,
          catIndex: catIndex >= 0 ? catIndex : null
        }));
      } catch (e) {
        //console.log('Error guardando selección:', e);
      }
    }

    if (onCategorySelect) {
      onCategorySelect(deptIdForSearch, categoryId, 0, deptName, catName, '');
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = getCategoryUrl(category);
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        window.location.href = url;
      }
    } else if (navigation?.navigate) {
      navigation.navigate('Productos', { D: deptIdForSearch, C: categoryId, S: 0, deptName, catName, subcatName: '', ofe });
    }
    onClose();
  };

  // Handler para click en subcategorÃ­a
  const handleSubcategoryClick = (subcat) => {
    const deptId =
      activeCategory?.codDepto === 0 || activeCategory?.codDepto
        ? activeCategory?.codDepto
        : subcat.codDepto;
    const catId = activeCategory?.codcat || subcat.codcat;
    const subcatId = subcat.codsubcat;
    const deptName = activeDept?.departamento || '';
    const catName = activeCategory?.categoria || '';
    const subcatName = subcat.subcategoria || '';

    // Guardar Ãºltima selecciÃ³n en localStorage
    if (Platform.OS === 'web') {
      try {
        localStorage.setItem('lastCategorySelection', JSON.stringify({
          deptId: activeDept?.cod || deptId,
          catIndex: hoveredCategory
        }));
      } catch (e) {
        //console.log('Error guardando selecciÃ³n:', e);
      }
    }

    if (onCategorySelect) {
      onCategorySelect(deptId, catId, subcatId, deptName, catName, subcatName);
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams();
      searchParams.set('D', String(deptId));
      searchParams.set('C', String(catId));
      searchParams.set('S', String(subcatId));
      searchParams.set('deptName', deptName);
      searchParams.set('catName', catName);
      searchParams.set('subcatName', subcatName);
      searchParams.set('codBanner', '0');

      const url = `/productos?${searchParams.toString()}`;
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        window.location.href = url;
      }
    } else if (navigation?.navigate) {
      navigation.navigate('Productos', { D: deptId, C: catId, S: subcatId, deptName, catName, subcatName });
    }
    onClose();
  };

  if (!visible) return null;

  // Contenido del menÃº cascada
  const menuContent = (
    <View 
      ref={containerRef}
      style={[
        styles.menuContainer,
        anchorPosition && { top: anchorPosition.top, left: anchorPosition.left }
      ]}
    >
      {/* Nivel 1: Departamentos */}
      <View style={styles.level1}>
        <ScrollView style={styles.levelScroll} showsVerticalScrollIndicator={false}>
          {loading ? (
            <Text style={styles.loadingText}>Cargando...</Text>
          ) : error ? (
            <Text style={styles.errorText}>{error}</Text>
          ) : (
            departments.map((dept) => (
              <View
                key={dept.cod}
                style={[
                  styles.menuItem,
                  hoveredDepartment === dept.cod && styles.menuItemActive
                ]}
                onMouseEnter={() => {
                  setHoveredDepartment(dept.cod);
                  setHoveredCategory(null);
                }}
              >
                <Text style={[
                  styles.menuItemText,
                  hoveredDepartment === dept.cod && styles.menuItemTextActive,                  dept?.tipo === 1 && styles.tipoBold,                  (dept?.tipo ?? 0) >= 2 && styles.tipoBoldRed,
                  
                ]}>
                  {capitalizeText(dept.departamento)}
                </Text>
                {dept.categorias?.length > 0 && (
                  <Text style={styles.arrow}>›</Text>
                )}
              </View>
            ))
          )}
        </ScrollView>
      </View>

      {/* Nivel 2: CategorÃ­as en 2 columnas con subcategorÃ­as debajo */}
      {hoveredDepartment !== null && activeCategories.length > 0 && (
        <View style={styles.level2}>
          {/* Header con nombre del departamento y lÃ­nea roja debajo */}
          <View style={styles.deptHeader}>
            <Text style={styles.deptHeaderText}>{activeDept?.departamento?.toUpperCase()}</Text>
            <View style={styles.deptHeaderRedLine} />
          </View>
          
          <ScrollView style={styles.levelScroll} showsVerticalScrollIndicator={false}>
            <View style={styles.columnsContainer}>
              {/* Columna 1 - Ã­ndices pares */}
              <View style={styles.categoryColumn}>
                {activeCategories.map((category, index) => {
                  if (index % 2 !== 0) return null;
                  return (
                    <View key={`cat-${index}-${category.codcat}`} style={styles.categoryWrapper}>
                      <TouchableOpacity
                        style={[styles.categoryItem, hoveredCategoryForZoom === index && styles.categoryItemHover]}
                        onPress={() => {
                          // Si no tiene subcategorías, buscar directamente
                          if (!category.subCategorias || category.subCategorias.length === 0) {
                            handleCategoryClick(category);
                          } else {
                            // Si tiene subcategorías, desplegar/ocultar
                            setHoveredCategory(hoveredCategory === index ? null : index);
                          }
                        }}
                        onMouseEnter={() => setHoveredCategoryForZoom(index)}
                        onMouseLeave={() => setHoveredCategoryForZoom(null)}
                      >
                        <Text style={[
                          styles.categoryText,
                          hoveredCategory === index && styles.categoryTextActive,                          category?.tipo === 1 && styles.tipoBold,                          (category?.tipo ?? 0) >= 2 && styles.tipoBoldRed
                        ]}>
                          {category.categoria?.toUpperCase()}
                        </Text>
                        {hoveredCategory === index && (
                          <View style={styles.redUnderline} />
                        )}
                      </TouchableOpacity>
                      
                      {hoveredCategory === index && (
                        <View style={styles.subcategoriesContainer}>
                          {/* Enlace "Ver todo" como primer elemento */}
                          {Platform.OS === 'web' ? (
                            <a
                              key={`ver-todo-${index}`}
                              href={getCategoryUrl(category)}
                              style={{...styles.subcategoryItem, textDecoration: 'none', cursor: 'pointer', display: 'block'}}
                              onMouseEnter={() => setHoveredSubcategory(`${index}-ver-todo`)}
                              onMouseLeave={() => setHoveredSubcategory(null)}
                              onClick={(e) => {
                                if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
                                  e.preventDefault();
                                  handleCategoryClick(category);
                                }
                              }}
                            >
                              <Text style={[
                                styles.subcategoryText,
                                styles.verTodoText,
                                hoveredSubcategory === `${index}-ver-todo` && styles.subcategoryTextHover
                              ]}>
                                Ver todo
                              </Text>
                            </a>
                          ) : (
                            <TouchableOpacity
                              key={`ver-todo-${index}`}
                              style={styles.subcategoryItem}
                              onPress={() => handleCategoryClick(category)}
                              onMouseEnter={() => setHoveredSubcategory(`${index}-ver-todo`)}
                              onMouseLeave={() => setHoveredSubcategory(null)}
                            >
                              <Text style={[
                                styles.subcategoryText,
                                styles.verTodoText,
                                hoveredSubcategory === `${index}-ver-todo` && styles.subcategoryTextHover
                              ]}>
                                Ver todo
                              </Text>
                            </TouchableOpacity>
                          )}
                          {category.subCategorias?.length > 0 && category.subCategorias.map((subcat, subIndex) => (
                            Platform.OS === 'web' ? (
                              <a
                                key={`subcat-${subcat.codsubcat}-${subIndex}`}
                                href={getSubcategoryUrl(subcat)}
                                style={{...styles.subcategoryItem, textDecoration: 'none', cursor: 'pointer', display: 'block'}}
                                onMouseEnter={() => setHoveredSubcategory(`${index}-${subIndex}`)}
                                onMouseLeave={() => setHoveredSubcategory(null)}
                                onClick={(e) => {
                                  if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
                                    e.preventDefault();
                                    handleSubcategoryClick(subcat);
                                  }
                                }}
                              >
                                <Text style={[
                                  styles.subcategoryText,
                                  hoveredSubcategory === `${index}-${subIndex}` && styles.subcategoryTextHover
                                ]}>
                                  {capitalizeText(subcat.subcategoria)}
                                </Text>
                              </a>
                            ) : (
                              <TouchableOpacity
                                key={`subcat-${subcat.codsubcat}-${subIndex}`}
                                style={styles.subcategoryItem}
                                onPress={() => handleSubcategoryClick(subcat)}
                                onMouseEnter={() => setHoveredSubcategory(`${index}-${subIndex}`)}
                                onMouseLeave={() => setHoveredSubcategory(null)}
                              >
                                <Text style={[
                                  styles.subcategoryText,
                                  hoveredSubcategory === `${index}-${subIndex}` && styles.subcategoryTextHover
                                ]}>
                                  {capitalizeText(subcat.subcategoria)}
                                </Text>
                              </TouchableOpacity>
                            )
                          ))}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
              
              {/* Columna 2 - Ã­ndices impares */}
              <View style={styles.categoryColumn}>
                {activeCategories.map((category, index) => {
                  if (index % 2 !== 1) return null;
                  return (
                    <View key={`cat-${index}-${category.codcat}`} style={styles.categoryWrapper}>
                      <TouchableOpacity
                        style={[styles.categoryItem, hoveredCategoryForZoom === index && styles.categoryItemHover]}
                        onPress={() => {
                          // Si no tiene subcategorías, buscar directamente
                          if (!category.subCategorias || category.subCategorias.length === 0) {
                            handleCategoryClick(category);
                          } else {
                            // Si tiene subcategorías, desplegar/ocultar
                            setHoveredCategory(hoveredCategory === index ? null : index);
                          }
                        }}
                        onMouseEnter={() => setHoveredCategoryForZoom(index)}
                        onMouseLeave={() => setHoveredCategoryForZoom(null)}
                      >
                        <Text style={[
                          styles.categoryText,
                          hoveredCategory === index && styles.categoryTextActive,                          category?.tipo === 1 && styles.tipoBold,                          (category?.tipo ?? 0) >= 2 && styles.tipoBoldRed
                        ]}>
                          {category.categoria?.toUpperCase()}
                        </Text>
                        {hoveredCategory === index && (
                          <View style={styles.redUnderline} />
                        )}
                      </TouchableOpacity>
                      
                      {hoveredCategory === index && (
                        <View style={styles.subcategoriesContainer}>
                          {/* Enlace "Ver todo" como primer elemento */}
                          {Platform.OS === 'web' ? (
                            <a
                              key={`ver-todo-${index}`}
                              href={getCategoryUrl(category)}
                              style={{...styles.subcategoryItem, textDecoration: 'none', cursor: 'pointer', display: 'block'}}
                              onMouseEnter={() => setHoveredSubcategory(`${index}-ver-todo`)}
                              onMouseLeave={() => setHoveredSubcategory(null)}
                              onClick={(e) => {
                                if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
                                  e.preventDefault();
                                  handleCategoryClick(category);
                                }
                              }}
                            >
                              <Text style={[
                                styles.subcategoryText,
                                styles.verTodoText,
                                hoveredSubcategory === `${index}-ver-todo` && styles.subcategoryTextHover
                              ]}>
                                Ver todo
                              </Text>
                            </a>
                          ) : (
                            <TouchableOpacity
                              key={`ver-todo-${index}`}
                              style={styles.subcategoryItem}
                              onPress={() => handleCategoryClick(category)}
                              onMouseEnter={() => setHoveredSubcategory(`${index}-ver-todo`)}
                              onMouseLeave={() => setHoveredSubcategory(null)}
                            >
                              <Text style={[
                                styles.subcategoryText,
                                styles.verTodoText,
                                hoveredSubcategory === `${index}-ver-todo` && styles.subcategoryTextHover
                              ]}>
                                Ver todo
                              </Text>
                            </TouchableOpacity>
                          )}
                          {category.subCategorias?.length > 0 && category.subCategorias.map((subcat, subIndex) => (
                            Platform.OS === 'web' ? (
                              <a
                                key={`subcat-${subcat.codsubcat}-${subIndex}`}
                                href={getSubcategoryUrl(subcat)}
                                style={{...styles.subcategoryItem, textDecoration: 'none', cursor: 'pointer', display: 'block'}}
                                onMouseEnter={() => setHoveredSubcategory(`${index}-${subIndex}`)}
                                onMouseLeave={() => setHoveredSubcategory(null)}
                                onClick={(e) => {
                                  if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
                                    e.preventDefault();
                                    handleSubcategoryClick(subcat);
                                  }
                                }}
                              >
                                <Text style={[
                                  styles.subcategoryText,
                                  hoveredSubcategory === `${index}-${subIndex}` && styles.subcategoryTextHover
                                ]}>
                                  {capitalizeText(subcat.subcategoria)}
                                </Text>
                              </a>
                            ) : (
                              <TouchableOpacity
                                key={`subcat-${subcat.codsubcat}-${subIndex}`}
                                style={styles.subcategoryItem}
                                onPress={() => handleSubcategoryClick(subcat)}
                                onMouseEnter={() => setHoveredSubcategory(`${index}-${subIndex}`)}
                                onMouseLeave={() => setHoveredSubcategory(null)}
                              >
                                <Text style={[
                                  styles.subcategoryText,
                                  hoveredSubcategory === `${index}-${subIndex}` && styles.subcategoryTextHover
                                ]}>
                                  {capitalizeText(subcat.subcategoria)}
                                </Text>
                              </TouchableOpacity>
                            )
                          ))}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            </View>
          </ScrollView>
        </View>
      )}

    </View>
  );

  // En web, usar portal
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return ReactDOM.createPortal(menuContent, document.body);
  }

  return menuContent;
};

const styles = StyleSheet.create({
  menuContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 100,
      left: 180,
      zIndex: 99999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    } : {}),
  },
  level1: {
    width: 280,
    borderRightWidth: 1,
    borderRightColor: '#eee',
    backgroundColor: '#fafafa',
    paddingTop: 34,
    ...(Platform.OS === 'web' ? {
      height: '70vh',
    } : {
      maxHeight: 400,
    }),
  },
  level2: {
    width: 550,
    borderRightWidth: 1,
    borderRightColor: '#eee',
    backgroundColor: '#fff',
    ...(Platform.OS === 'web' ? {
      height: '70vh',
    } : {
      maxHeight: 400,
    }),
  },
  levelScroll: {
    flex: 1,
  },
  // Estilos para departamentos (nivel 1)
  menuItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  menuItemActive: {
    backgroundColor: '#fff',
    borderLeftWidth: 3,
    borderLeftColor: colores.rojo,
  },
  menuItemText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
    fontWeight: 'normal',
  },
  menuItemTextActive: {
    color: colores.rojo,
    fontWeight: '600',
  },
  arrow: {
    fontSize: 16,
    color: '#999',
    marginLeft: 8,
  },
  // Header del departamento
  deptHeader: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#fafafa',
  },
  deptHeaderText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#333',
  },
  deptHeaderRedLine: {
    height: 2,
    backgroundColor: colores.rojo,
    marginTop: 10,
  },
  // Contenedor de columnas
  columnsContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  categoryColumn: {
    flex: 1,
    borderLeftWidth: 2,
    borderLeftColor: colores.rojo,
    paddingHorizontal: 12,
    paddingTop: 8,
  },
  categoryWrapper: {
    marginBottom: 4,
  },
  categoryItem: {
    paddingVertical: 8,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  categoryItemHover: {
    ...(Platform.OS === 'web' ? { 
      transform: [{ scale: 1.05 }],
      transition: 'transform 0.2s ease-in-out',
    } : {}),
  },
  categoryItemActive: {
    // El estilo activo se maneja con la lÃ­nea roja
  },
  categoryText: {
    fontSize: 14,
    color: '#333',
    fontWeight: 'bold',
  },
  categoryTextActive: {
    color: colores.rojo,
  },
  redUnderline: {
    height: 2,
    backgroundColor: colores.rojo,
    marginTop: 4,
    width: '100%',
  },
  // SubcategorÃ­as debajo de categorÃ­a
  subcategoriesContainer: {
    paddingLeft: 8,
    paddingTop: 4,
    paddingBottom: 8,
  },
  subcategoryItem: {
    paddingVertical: 4,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  subcategoryText: {
    fontSize: 12,
    color: '#666',
    fontWeight: 'normal',
  },
  verTodoText: {
    fontWeight: '700',
    fontSize: 13,
  },
  subcategoryTextHover: {
    color: colores.rojo,
    fontSize: 13,
    fontWeight: '600',
  },
    tipoBold: { fontWeight: '700' },
  tipoBoldRed: { fontWeight: '700', color: colores.rojo },
loadingText: {
    padding: 20,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  errorText: {
    padding: 20,
    fontSize: 14,
    color: colores.rojo,
    textAlign: 'center',
  },
});

export default Categorias;



